import { useCallback, useEffect, useState } from "react";
import type { ShipmentDetail } from "api";
import { shipmentsApi } from "api";
import { isForbidden, isNotFound, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

/**
 * "missing" is the back office's 404. It answers the same for a shipment that
 * does not exist and for one another company owns, so ids cannot be probed —
 * and the page cannot tell the two apart either.
 */
export type ShipmentDetailStatus = LoadStatus | "missing";

type UseShipmentDetail = {
  shipment: ShipmentDetail | null;
  status: ShipmentDetailStatus;
  /** Ask again from scratch, showing the spinner — what the error state's Retry does. */
  retry: () => void;
  /** Repaint from a save's response instead of asking again. */
  replace: (shipment: ShipmentDetail) => void;
};

export const useShipmentDetail = (id: number, onSessionLost: () => void): UseShipmentDetail => {
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [status, setStatus] = useState<ShipmentDetailStatus>("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setShipment(null);
    setStatus("loading");

    shipmentsApi
      .show(id)
      .then(({ data }) => {
        if (cancelled) return;
        setShipment(data);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthenticated(error)) {
          onSessionLost();
          setStatus("unauthorized");
          return;
        }
        if (isNotFound(error)) {
          setStatus("missing");
          return;
        }

        setStatus(isForbidden(error) ? "unverified" : "failed");
      });

    return () => {
      cancelled = true;
    };
  }, [id, attempt, onSessionLost]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  return { shipment, status, retry, replace: setShipment };
};
